import { apiRequest } from './client'
import { SILKREELS_API_BASE } from './config'
import { ensureFreshAccessToken, readAccessToken } from './authSession'

export interface PromptCategory {
  id: string
  name: string
  sort_order: number
  item_count?: number
}

export interface PromptItem {
  id: string
  category_id?: string
  category_name?: string
  category_code?: string
  name: string
  content: string
  remark: string
  effect_media_url: string
  effect_media_kind: '' | 'image' | 'video'
  sort_order: number
  created_at: string
  updated_at: string
}

export interface PromptListResponse {
  total: number
  limit?: number
  offset?: number
  items: PromptItem[]
}

export interface PromptCategoryListResponse {
  total: number
  items: PromptCategory[]
}

const BASE = '/api/v1/studio/prompt-library'

export function fetchTenantPromptCategories() {
  return apiRequest<{ items: PromptCategory[] }>(`${BASE}/tenant/categories`)
}

export function fetchTenantPromptItems(params?: {
  category_code?: string
  q?: string
  limit?: number
  offset?: number
}) {
  const qs = new URLSearchParams()
  if (params?.category_code) qs.set('category_code', params.category_code)
  if (params?.q) qs.set('q', params.q)
  if (params?.limit != null) qs.set('limit', String(params.limit))
  if (params?.offset != null) qs.set('offset', String(params.offset))
  const suffix = qs.toString() ? `?${qs}` : ''
  return apiRequest<PromptListResponse>(`${BASE}/tenant/items${suffix}`)
}

export function fetchMyPromptCategories() {
  return apiRequest<PromptCategoryListResponse>(`${BASE}/me/categories`)
}

export function createMyPromptCategory(body: { name: string; sort_order?: number }) {
  return apiRequest<PromptCategory>(`${BASE}/me/categories`, { method: 'POST', body })
}

export function updateMyPromptCategory(id: string, body: { name?: string; sort_order?: number }) {
  return apiRequest<PromptCategory>(`${BASE}/me/categories/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  })
}

export function deleteMyPromptCategory(id: string) {
  return apiRequest<{ ok: boolean }>(`${BASE}/me/categories/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function fetchMyPromptItems(params?: {
  category_id?: string
  q?: string
  limit?: number
  offset?: number
}) {
  const qs = new URLSearchParams()
  if (params?.category_id) qs.set('category_id', params.category_id)
  if (params?.q) qs.set('q', params.q)
  if (params?.limit != null) qs.set('limit', String(params.limit))
  if (params?.offset != null) qs.set('offset', String(params.offset))
  const suffix = qs.toString() ? `?${qs}` : ''
  return apiRequest<PromptListResponse>(`${BASE}/me/items${suffix}`)
}

export function createMyPromptItem(body: {
  category_id?: string
  name: string
  content: string
  remark?: string
  effect_media_url?: string
  effect_media_kind?: '' | 'image' | 'video'
  sort_order?: number
}) {
  return apiRequest<PromptItem>(`${BASE}/me/items`, { method: 'POST', body })
}

export function updateMyPromptItem(
  id: string,
  body: {
    category_id?: string
    name?: string
    content?: string
    remark?: string
    effect_media_url?: string
    effect_media_kind?: '' | 'image' | 'video'
    sort_order?: number
  },
) {
  return apiRequest<PromptItem>(`${BASE}/me/items/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  })
}

export function deleteMyPromptItem(id: string) {
  return apiRequest<{ ok: boolean }>(`${BASE}/me/items/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function uploadPromptEffectMedia(file: File, kind: 'image' | 'video') {
  await ensureFreshAccessToken()
  const token = readAccessToken()
  const form = new FormData()
  form.append('file', file)
  form.append('kind', kind)
  const res = await fetch(`${SILKREELS_API_BASE}/api/v1/assets/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  const json = (await res.json()) as { code: number; message?: string; data?: { url?: string } }
  if (!res.ok || json.code !== 0) {
    throw new Error(json.message || 'upload failed')
  }
  return json.data?.url ?? ''
}
